import { message } from 'antd';
import { put, takeLatest, select } from 'redux-saga/effects';
import { ProjectPhase } from 'api/project';
import { PositionData } from 'components/organisms/templates/projects/commons/rearrange/types';
import { StoreActionProps } from 'data/core';
import { PhaseEntityType, extractParts } from 'data/projects/phase-entity';
import { logger } from 'services';
import { getAutoCorrections } from 'utils/date-shift';
import {
  getPhaseList,
  getSprintList,
  getTaskList,
  getTemplatePhaseUpdates,
  getTemplateTaskUpdates,
} from 'utils/template-date-shift';
import { updateProjectTemplateParentOfTask } from '../..';
import { rearrangeEntitiesInProjectTemplate } from '../../actions';
import {
  selectAllTemplateProjectPhase,
  selectAllTemplateSprint,
  selectAllTemplateTask,
  selectTemplateEntity,
  selectTemplateTask,
} from '../../selectors';
import {
  makeDateShiftsForProjectPhases,
  makeDateShiftsForTasks,
  updateProjectPhaseTemplateEntityOrder,
} from '../../slices';
import { ProjectTemplateProjectPhase, ProjectTemplateTask } from '../../types';

function* rearrangeTaskInTemplateSaga(
  action: StoreActionProps<PositionData[]>,
) {
  try {
    const { payload: actions } = action;

    for (let i = 0; i < actions.length; i += 1) {
      const { entityId, parent, insertAt } = actions[i];
      const { id, entityType } = extractParts(entityId);

      const entity: ProjectTemplateTask = yield select(
        selectTemplateEntity(entityId),
      );

      yield put(
        updateProjectPhaseTemplateEntityOrder({
          entity,
          entityId,
          ...parent,
          insertAt,
        }),
      );

      if (entityType === PhaseEntityType.Task) {
        const task: ProjectTemplateTask = yield select(selectTemplateTask(id));

        yield put(
          updateProjectTemplateParentOfTask({
            task,
            ...parent,
            insertAt,
          }),
        );
      }
    }
  } catch (error) {
    logger.error('rearrangeTask failed', { error });

    message.error('There was an error in rearranging the task');
  }
}

export type TemplateTask = Omit<ProjectTemplateTask, 'projectPhase'> & {
  projectPhase: ProjectPhase | ProjectTemplateProjectPhase;
};

function* dateShiftOnTasksRearrangeSaga(
  action: StoreActionProps<PositionData[]>,
) {
  try {
    const { payload: actions } = action;
    let phases: { [key: string]: ProjectTemplateProjectPhase } = yield select(
      selectAllTemplateProjectPhase(),
    );
    let tasks: {
      [key: string]: TemplateTask;
    } = yield select(selectAllTemplateTask());

    // TODO: need to refactor to handle sprints rearrange
    const sprints = yield select(selectAllTemplateSprint());

    for (let i = 0; i < actions.length; i += 1) {
      const { entityId, parent } = actions[i];
      const { id: taskId, entityType } = extractParts(entityId);

      if (entityType === PhaseEntityType.Task) {
        /**
         * Update parent and child references
         */
        const task = tasks[taskId];
        const {
          projectPhase: newParentPhase = {} as ProjectTemplateProjectPhase,
          parentTask: newParentTask = {} as ProjectTemplateTask,
        } = {
          ...parent,
        } as any;

        /**
         * Remove task reference from current parent
         */
        if (task?.parentTask?.taskId) {
          const currentParentTask = tasks[task.parentTask.taskId];

          tasks = {
            ...tasks,
            [task.parentTask.taskId]: {
              ...currentParentTask,
              subTasks: currentParentTask.subTasks.filter(
                (subTaskRef: ProjectTemplateTask | number) =>
                  subTaskRef !== taskId,
              ),
            },
          };
        } else if (task?.projectPhase?.projectPhaseId) {
          const currentProjectPhase = phases[task.projectPhase.projectPhaseId];

          phases = {
            ...phases,
            [task.projectPhase.projectPhaseId]: {
              ...currentProjectPhase,
              tasks: currentProjectPhase.tasks.filter(
                (taskRef: ProjectTemplateTask | number) => taskRef !== taskId,
              ),
            },
          };
        }
        /**
         * Add task reference in new parent and update parent reference in task
         */
        if (newParentPhase?.projectPhaseId) {
          const _phase = phases[newParentPhase.projectPhaseId];

          phases = {
            ...phases,
            [newParentPhase.projectPhaseId]: {
              ..._phase,
              tasks: [taskId, ..._phase.tasks],
            },
          };
          tasks = {
            ...tasks,
            [taskId]: {
              ...task,
              projectPhase: _phase,
              parentTask: undefined,
            },
          };
        } else if (newParentTask?.taskId) {
          const _task = tasks[newParentTask.taskId];

          tasks = {
            ...tasks,
            [newParentTask.taskId]: {
              ..._task,
              subTasks: [taskId, ..._task.subTasks],
            },
            [taskId]: {
              ...task,
              parentTask: _task,
              projectPhase: undefined,
            },
          };
        }
      }
      const taskList = getTaskList(Object.values(tasks));
      const [phaseUpdates, taskUpdates] = getAutoCorrections(
        getPhaseList(Object.values(phases)),
        taskList,
        getSprintList(Object.values(sprints)),
        { allowWeekend: true, enabledNewDependencies: false },
      );

      yield put(
        makeDateShiftsForProjectPhases(getTemplatePhaseUpdates(phaseUpdates)),
      );
      yield put(
        makeDateShiftsForTasks(getTemplateTaskUpdates(taskUpdates, taskList)),
      );
    }
  } catch (error) {
    logger.error('Date shifting during rearrangeTask failed', { error });

    message.error('There was an error in rearranging the task');
  }
}

export function* watchRearrangeTaskInProjectTemplateSaga(): Generator {
  yield takeLatest(
    rearrangeEntitiesInProjectTemplate.type,
    rearrangeTaskInTemplateSaga,
  );

  yield takeLatest(
    rearrangeEntitiesInProjectTemplate.type,
    dateShiftOnTasksRearrangeSaga,
  );
}
